"use client";

import { cn } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Image from '@/components/ui/Image';
import { useEffect, useState } from "react";
import parse from "html-react-parser";
import Link from "next/link";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { SectionWrapper } from "../components/SectionWraper";

interface GalleryItem {
  image?: string;
  imageAltText?: string;
  title?: string;
  content?: string;
  subtitle?: string;
  icon?: string;
  link?: string;
}

interface Gallery4Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  galleryItems?: GalleryItem[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Gallery4 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Gallery 1",
  themeChangeOnScroll = false,
  title,
  content,  
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  galleryItems,
  attributes,
}: Gallery4Props) => {  
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  useEffect(() => {
    if (!carouselApi) {
      return;
    }
    const updateSelection = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    };
    updateSelection();
    carouselApi.on("select", updateSelection);
    return () => {
      carouselApi.off("select", updateSelection);
    };
  }, [carouselApi]);

  return (
    <SectionWrapper 
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="gallery4"
      attributes={attributes}
    > 
      <div className="container">
        <div className="flex flex-col">
          {title && (
            <h2 className="max-w-[500px] lg:max-w-[800px]">
              {parse(title)}
            </h2>
          )}
          {content && (
            <div className="mt-2 paragraph-lg text-muted-foreground">
              {parse(content)}
            </div>
          )}
          {hasButton && (
            <div>
              <Button asChild variant={buttonStyle} size="lg" className="mt-6 w-full sm:w-auto">
                <Link href={buttonLink ?? ""}>
                  {buttonText}
                </Link>
              </Button>
              {microCopy && (
                <div className="mt-2 paragraph-sm text-muted-foreground text-center sm:text-left">
                  {microCopy}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="relative w-full overflow-hidden mt-20">
        <Carousel
          setApi={setCarouselApi}
          opts={{
            align: "start",
            breakpoints: {
              "(max-width: 768px)": {
                dragFree: true,
              },
            },
          }}
          data-aos="fade-left"
        >
          <CarouselContent 
            className={cn(
              "mr-4 ml-0",
              "md:mr-8 md:ml-2",
              "2xl:mr-[max(0rem,calc(100vw/2-1336px/2))] 2xl:ml-[max(0rem,calc(100vw/2-1336px/2-24px))]"
            )}
          >
            {galleryItems?.map((item, index) => (
              <CarouselItem 
                key={index} 
                className="pl-4 md:pl-6 max-w-[328px] md:max-w-[500px]"
              >
                <div className="group/gallery1 flex flex-col justify-between">
                  <div className="flex aspect-[500/328] overflow-clip rounded-xl">
                    <div className="flex-1">
                      <div className="relative h-full w-full origin-bottom transition duration-300 group-hover/gallery1:scale-105">
                        {item.image && (
                          <Image
                            src={item.image}  
                            alt={item.imageAltText ?? ""}
                            title={item.imageAltText ?? ""}
                            className="h-full w-full object-cover object-center"
                            fill
                            sizes="500px"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col py-6 gap-10">
                  <h5>
                    {item.title}
                  </h5>
                  <div className="flex flex-col gap-1">
                    {item.subtitle && (
                      <div className="paragraph-lg font-bold">
                        {parse(item.subtitle ?? "")}
                      </div>
                    )}
                    {item.content && (
                      <div className="paragraph-base text-muted-foreground">
                        {parse(item.content ?? "")}
                      </div>
                    )}
                  </div>
                  {(item.link && item.icon) ? (
                    <Button asChild variant="outline" size="iconSm" grayBorder>
                      <Link href={item.link} className="paragraph-base text-muted-foreground">
                        {item.icon === "linkedin" ? (
                          <Image src="/icon-linkedin-blue.svg" alt="LinkedIn" width={24} height={24} />
                        ) : (
                          <span className="material-symbols-outlined">
                            {item.icon}
                          </span>
                        )}
                      </Link>
                    </Button>
                  ) : (
                    item.icon && (
                      <Button asChild variant="outline" size="iconSm" grayBorder>
                        {item.icon === "linkedin" ? (
                          <Image src="/icon-linkedin-blue.svg" alt="LinkedIn" width={24} height={24} />
                        ) : (
                          <span className="material-symbols-outlined">
                            {item.icon}
                          </span>
                        )}
                      </Button>
                    )
                  )}
                  
                </div>
               
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
      <div className="container mt-8 shrink-0 items-center justify-start gap-2 hidden lg:flex">
        <Button
          size="iconSm"
          variant="outline"
          grayBorder
          onClick={() => {
            carouselApi?.scrollPrev();
          }}
          disabled={!canScrollPrev}
          className="disabled:pointer-events-auto"
        >
          <span className="material-symbols-outlined">
            arrow_back  
          </span>
        </Button>
        <Button
          size="iconSm"
          variant="outline"
          grayBorder
          onClick={() => {
            carouselApi?.scrollNext();
          }}
          disabled={!canScrollNext}
          className="disabled:pointer-events-auto"
        >
          <span className="material-symbols-outlined">
            arrow_forward
          </span>
        </Button>
      </div>
    </SectionWrapper>
  );
};

export { Gallery4 };



